/**
 * @lodex-pro/shared/errors/catalog — ERROR_CATALOG + ErrorCode + ErrorResponse.
 *
 * Phase 3 / Plan 02 — D-13, D-14, D-15, D-16. Single source of truth for the
 * API error envelope. Mobile/web import this catalog so client code can branch
 * on `code` and localize `userMessage` later without the API needing
 * language-aware responses.
 *
 * Plan 07 ships the global Fastify setErrorHandler that maps every thrown
 * error to ErrorResponse using ERROR_CATALOG[code].httpStatus + .userMessage.
 *
 * Sub-reasons (e.g. JWT expired vs wrong-issuer) are NEVER in userMessage —
 * D-05 + T-ERR-01 mitigation. Sub-reasons go to Pino server logs only.
 *
 * Phase 15 followup / DEC-154 — extended: 3 codes (RATE_LIMIT_EXCEEDED,
 * CAPTCHA_REQUIRED, CAPTCHA_VERIFICATION_FAILED). Total: 60.
 *
 * Spec: master spec §31 (three-audience model), CONTEXT D-13/D-14/D-15/D-16.
 */

/**
 * D-14 — the canonical API error codes.
 *
 * `as const satisfies Record<string, { httpStatus: number; userMessage: string }>`
 * ensures every entry has both fields correctly typed at compile time, while
 * preserving the literal types of each field value (needed for ErrorCode =
 * keyof typeof ERROR_CATALOG to resolve to a string union, not just string).
 */
export const ERROR_CATALOG = {
  UNAUTHENTICATED: { httpStatus: 401, userMessage: 'Sign in to continue.' },
  INVALID_TOKEN: { httpStatus: 401, userMessage: 'Your session expired. Please sign in again.' },
  TENANT_NOT_SELECTED: { httpStatus: 403, userMessage: 'Pick a tenant to continue.' },
  // Operator portal PR 11a (design §4.6) — the sign-in came from the wrong Cognito pool for this
  // kind of company: an operator-pool token acting outside an OPERATOR company, or an OPERATOR
  // company reached from another pool. On the mobile app this is the wrong Driver / Pumper choice.
  AUTH_POOL_MISMATCH: {
    httpStatus: 403,
    userMessage:
      'This sign-in can’t be used for this company. Sign out and sign in with the right account type.',
  },
  /**
   * Quick 260816-vr9 — distinct from TENANT_NOT_SELECTED on purpose.
   *
   * A deactivated member and a user who has not picked a tenant are mechanically
   * identical at the point the error is raised (both resolve to partial auth
   * context), so both used to surface as "Pick a tenant to continue." — telling
   * someone whose access was revoked to go pick a tenant, which they cannot do.
   *
   * Same HTTP status; the difference is entirely in what the human is told.
   */
  ACCESS_REVOKED: {
    httpStatus: 403,
    userMessage: 'Your access to this tenant has been removed. Contact your administrator.',
  },
  /**
   * DEC-349 (OPEN-134) — the user's active company is suspended (tenants.deleted_at,
   * set from the Paelode admin console). Same 403 as TENANT_NOT_SELECTED, which is
   * what it used to surface as: the suspend gate nulled the tenant, so the clients
   * read "pick a tenant", auto-switched straight back into the suspended company and
   * rendered an empty app as "Unknown user". Nothing the user can do fixes it.
   */
  TENANT_SUSPENDED: {
    httpStatus: 403,
    userMessage: "This company's account is suspended. Contact your administrator.",
  },
  // DEC-313 — the active tenant holds more than one role for this user, so no
  // role can be resolved without guessing. Unreachable once migration 0124's
  // UNIQUE (user_id, tenant_id) is applied; it exists so that an environment
  // missing that index refuses instead of resolving a role at random.
  ROLE_AMBIGUOUS: {
    httpStatus: 403,
    userMessage:
      'Your account has more than one role in this company, so we cannot tell which to use. Contact your administrator.',
  },
  PERMISSION_DENIED: { httpStatus: 403, userMessage: "You don't have access to this action." },
  VALIDATION_FAILED: { httpStatus: 400, userMessage: 'Some fields are invalid.' },
  // Phase 10 / Task 10.6 — tenant print-logo upload could not be decoded/converted.
  LOGO_CONVERSION_FAILED: {
    httpStatus: 422,
    userMessage:
      'Logo could not be converted. Use a high-contrast black-and-white image for best results.',
  },
  // DEC-224 — the driver's one-time print signature could not be decoded, or decoded
  // to something with no ink in it. The blank case is the important one: a blank
  // canvas export is a structurally valid PNG, so it passes every magic-number and
  // size check, and under "capture once, print forever" it would then print on EVERY
  // ticket that driver ever produces. This is the gate that stops that.
  SIGNATURE_CONVERSION_FAILED: {
    httpStatus: 422,
    userMessage: 'That signature did not come through. Please sign again, pressing firmly.',
  },
  TENANT_MISMATCH: {
    httpStatus: 403,
    userMessage: 'This action is not allowed for your account.',
  },
  INTERNAL_ERROR: { httpStatus: 500, userMessage: 'Something went wrong. Try again.' },

  // --- Phase 4 ADDITIONS ---
  MISSING_DEVICE_ID: {
    httpStatus: 400,
    userMessage: 'Your device is not recognized. Try signing in again.',
  },
  NEW_DEVICE_CHALLENGE: {
    httpStatus: 401,
    userMessage: 'New device — enter the code we sent to your phone.',
  },
  PIN_LOCKED: { httpStatus: 423, userMessage: 'Too many wrong PINs. Try again in {seconds}.' },
  ROSTER_STALE: {
    httpStatus: 409,
    userMessage: 'Driver list is out of date — connect to refresh.',
  },
  ROSTER_DEVICE_REVOKED: {
    httpStatus: 403,
    userMessage: 'This tablet has been deactivated. See your dispatcher.',
  },
  PIN_NOT_SET: {
    httpStatus: 404,
    userMessage: 'You need to set a PIN before logging in offline. Connect once to set it up.',
  },
  REFRESH_TOKEN_EXPIRED: {
    httpStatus: 401,
    userMessage: 'Your session expired. Please sign in again.',
  },
  REFRESH_TOKEN_REVOKED: {
    httpStatus: 401,
    userMessage: 'You were signed out from another device. Please sign in again.',
  },
  ENROLLMENT_TOKEN_INVALID: {
    httpStatus: 400,
    userMessage: "This QR code can't be used. Get a fresh one from your dispatcher.",
  },
  ENROLLMENT_TOKEN_EXPIRED: {
    httpStatus: 410,
    userMessage: 'This QR code expired. Get a fresh one from your dispatcher.',
  },
  MFA_SETUP_REQUIRED: {
    httpStatus: 403,
    userMessage: 'Set up two-factor authentication to continue.',
  },
  MFA_CODE_INVALID: { httpStatus: 400, userMessage: "That code didn't match. Try again." },
  MFA_CODE_EXPIRED: { httpStatus: 410, userMessage: 'That code expired. Tap "Send new code".' },

  // --- Phase 5 ADDITIONS (Job lifecycle) ---
  NOT_FOUND: { httpStatus: 404, userMessage: 'The requested resource was not found.' },
  JOB_TRANSITION_FORBIDDEN: {
    httpStatus: 409,
    userMessage: 'This job cannot move to that status from its current state.',
  },

  // --- Phase 6 ADDITIONS (Run-ticket capture) ---
  JOB_NOT_RESUMABLE: {
    httpStatus: 409,
    userMessage: 'This job cannot be resumed from its current state.',
  },
  NO_ACTIVE_SHIFT: {
    httpStatus: 409,
    userMessage: 'Start a shift before recording a clock event.',
  },
  /**
   * DEC-278 — a driver tried to reject a job they had already ACCEPTED. Reject
   * is a before-accept action only; after accepting, the driver calls dispatch.
   * 409 rather than 403: the authority is fine, the job's state is not.
   */
  JOB_ALREADY_ACCEPTED: {
    httpStatus: 409,
    userMessage: "You've already accepted this job. Call dispatch to hand it back.",
  },
  /**
   * DEC-278 — the hauler has driver-side reject switched off
   * (`tenants.driver_job_reject_enabled = false`). Distinct from
   * PERMISSION_DENIED: the DRIVER role does hold REJECT_JOB, so this is a
   * per-tenant policy, not a matrix decision, and the client renders a
   * different message.
   */
  DRIVER_REJECT_DISABLED: {
    httpStatus: 403,
    userMessage: 'Your company has turned off driver job rejection. Call dispatch.',
  },
  /**
   * DEC-273 — a DISPATCH_MANAGER+ tried to resume a driver's paused job while
   * that driver has no open shift. Distinct from NO_ACTIVE_SHIFT, whose message
   * ("Start a shift...") addresses the DRIVER resuming their own job; here the
   * actor is a dispatcher and the person off shift is someone else.
   */
  DRIVER_NOT_ON_SHIFT: {
    httpStatus: 409,
    userMessage: 'That driver is not on an active shift, so their clock cannot be resumed.',
  },
  UPLOAD_PRESIGN_FAILED: {
    httpStatus: 502,
    userMessage: 'Upload failed. Try again.',
  },
  UPLOAD_TOO_SMALL: {
    httpStatus: 400,
    userMessage: 'That upload did not come through. Capture it again.',
  },
  /**
   * 86bc6kaax — the object is big enough to clear the floor but does not carry
   * the PNG signature, so it is not an image. Distinct from UPLOAD_TOO_SMALL
   * because the sizes do not overlap: a degenerate data-URL decode above 67
   * bytes passes the floor and is caught only here. Permanent, like
   * UPLOAD_TOO_SMALL — re-PUTting the same bytes cannot change the verdict.
   */
  UPLOAD_NOT_A_PNG: {
    httpStatus: 400,
    userMessage: 'That upload is not a valid image. Capture it again.',
  },
  /**
   * 86bc6kaax follow-up — the photo path's equivalent of UPLOAD_NOT_A_PNG. The
   * assembled multipart object cleared MIN_JPEG_BYTES but carries no JPEG SOI
   * prefix, so it is not an image and must not be appended to
   * haul_captures.photo_s3_keys. Permanent: the bytes are already in S3 and
   * re-completing the same upload cannot change the verdict.
   */
  UPLOAD_NOT_A_JPEG: {
    httpStatus: 400,
    userMessage: 'That photo is not a valid image. Take it again.',
  },
  S3_KEY_MISMATCH: {
    httpStatus: 400,
    userMessage: 'Upload key does not match the job. Try again.',
  },
  OPERATOR_HAS_NO_ABBREVIATION: {
    httpStatus: 409,
    userMessage: 'This operator is missing a customer abbreviation. Contact dispatch.',
  },
  HAUL_ID_ISSUE_FAILED: {
    httpStatus: 500,
    userMessage: 'Could not issue a haul ID. Try again.',
  },
  // DEC-024 — request captureMethod does not match the job_type's captureMethod.
  CAPTURE_METHOD_MISMATCH: {
    httpStatus: 400,
    userMessage: 'The capture method does not match this job type. Contact dispatch.',
  },
  // DEC-025 — a required per-tank field is missing on one or more tanks.
  // Required-ness is policy-driven via job_types.require_water_volume /
  // require_oil_volume / require_gauge_readings (SINGLE_GAUGE only). The error
  // response's `fields` array enumerates which tanks at which positions are
  // missing which fields (path: 'tanks[<position>].<fieldName>', reason:
  // 'required_by_job_type').
  CAPTURE_REQUIRED_FIELD_MISSING: {
    httpStatus: 400,
    userMessage: 'One or more required fields are missing on the capture.',
  },
  // DEC-026 (2026-05-17, quick 260517-d8h) — capture POST referenced a productId
  // that is NOT an active, tenant-scoped product. Either the UUID does not
  // exist, points at a different tenant (cross-tenant — RLS hides the row from
  // the validation SELECT), or points at an is_active=false row. The error
  // response's `fields` array (when populated) enumerates which tanks at which
  // positions referenced the bad product.
  INVALID_PRODUCT_ID: {
    httpStatus: 400,
    userMessage: 'One or more tanks reference an unknown or inactive product.',
  },

  // --- Phase 06.1 ADDITIONS (DEC-027 + DEC-028) — lifecycle events + disposal flow ---
  // DEC-028 — body GPS lat/lng = (0,0) rejected at the API layer. §16: (0,0)
  // is never a real value, only a rejected sentinel. Mobile sends NULL for
  // no-fix; (0,0) only arrives via tampering or downstream conversion bug.
  GPS_REJECTED_ZERO_ZERO: {
    httpStatus: 400,
    userMessage: 'Location reading was invalid. Try again or capture with location off.',
  },
  // RETIRED by DEC-352 — NO route throws this any more. DEC-028 rejected events
  // whose device occurred_at was more than 24 hours from server now(); that lost
  // real offline work, so such a record is now accepted and flagged TIME_CHECK in
  // review. Kept because phones still hold queue rows rejected with this code
  // (the mobile drain re-sends them once) and older builds still name it.
  CLOCK_SKEW_TOO_LARGE: {
    httpStatus: 400,
    userMessage: 'Your device clock is off by more than 24 hours. Check the time settings.',
  },
  // DEC-027 — request actualDisposalFacilityId points at a row that does not
  // exist or has is_active=false in the platform-managed disposal_facilities
  // catalog.
  INVALID_DISPOSAL_FACILITY_ID: {
    httpStatus: 400,
    userMessage: 'That disposal facility is unknown or inactive.',
  },
  // DEC-027 — event or transition does not match the §8 state the job is in
  // (e.g. driver posts ARRIVED_DISPOSAL while still ARRIVED_PICKUP, or
  // attempts DISPOSAL_COMPLETE before ARRIVED_DISPOSAL).
  EVENT_OUT_OF_SEQUENCE: {
    httpStatus: 409,
    userMessage: "That action does not match this job's current state.",
  },
  // DEC-027 / D-15 — driver attempted a disposal transition on a job whose
  // timeclock is STOPPED or never started. §11 event-chain timeclock check;
  // sub-reason goes to Pino warn log per D-05 + T-ERR-01.
  JOB_NOT_ACTIVE: {
    httpStatus: 409,
    userMessage: 'Start the clock on this job before recording this action.',
  },
  // DEC-027 / D-15 — driver attempted a disposal transition on a paused job.
  // §11 event-chain PAUSED check; resume must be the next action.
  JOB_PAUSED: {
    httpStatus: 409,
    userMessage: 'Resume this job to record this action.',
  },

  // === Phase 06.2 additions ===

  // HOURLY-02 — thrown by disposal-complete endpoint (Plan 06.2-15) when the
  // job_type.billing_method = 'HOURLY' and driverTotalTimeMinutes is missing.
  DRIVER_TOTAL_TIME_REQUIRED: {
    httpStatus: 400,
    userMessage: 'Enter the total time for this hourly job before completing.',
  },

  // BILLCFG-04 — thrown by capture endpoint (Plan 06.2-16) when
  // operators.require_reference_id = true and the reference field is missing
  // or empty on the request body.
  REFERENCE_ID_REQUIRED: {
    httpStatus: 400,
    userMessage: 'A reference number is required for this operator.',
  },

  // JOBFLOW-04 — thrown by start-work endpoint (Plan 06.2-13 Task 4) when the
  // driver has another job whose most-recent timeclock event is STARTED|RESUMED.
  // Enforces the single-active-clock invariant per master spec §11 / DEC-009 —
  // a driver may not be on two clocks at once. The Resume endpoint (Plan 06.2-14)
  // handles the auto-pause-on-switch path that fixes this when the driver
  // chooses to switch jobs; start-work rejects so the driver makes the choice
  // explicitly rather than silently abandoning the other job's clock.
  MULTIPLE_ACTIVE_CLOCKS: {
    httpStatus: 409,
    userMessage: 'Pause your other active job before starting a new one.',
  },

  // === Phase 07 additions ===

  // SYNC-05 / T-07-13 — the sync receiver rejected a job_transition push
  // because the targetStatusCode is NOT in the SERVER_CONFIRMED allowlist.
  // Kills the legacy `target.ssJobStatusId = source.ssJobStatusId` pattern
  // that could drive a job into IN_REVIEW, CANCELLED, or INVOICED from
  // a mobile push. The mobile drain loop must not retry this payload —
  // it is a permanent rejection.
  JOB_STATUS_NOT_ALLOWED: {
    httpStatus: 400,
    userMessage: 'That job status cannot be set from a mobile push.',
  },

  // === Phase 26 / Task 26.3 additions (DEC-147 #6) ===

  // Task 26.3 — request body/payload set BOTH actualDisposalFacilityId AND
  // actualDropoffHaulerLocationId (or both plannedDisposal + plannedDropoff).
  // A dropoff-completed job resolves to exactly one dropoff target; specifying
  // both is a client-side bug (the picker should have narrowed to one).
  MULTIPLE_DROPOFF_TARGETS: {
    httpStatus: 400,
    userMessage: 'Pick a single dropoff destination.',
  },
  // Task 26.3 — request pointed at a hauler_location that (a) does not exist
  // in the tenant, (b) is inactive, or (c) has can_dropoff=false. The
  // dropoff-target resolver enforces can_dropoff=true for hauler_locations
  // (disposal_facilities are ALWAYS dropoff-capable and use
  // INVALID_DISPOSAL_FACILITY_ID instead).
  INVALID_DROPOFF_HAULER_LOCATION_ID: {
    httpStatus: 400,
    userMessage: 'That dropoff location is unknown, inactive, or not set up for dropoff.',
  },
  // SWD destination A1 (0208) — a disposal-well dropoff that is not one of this company's
  // active authorized disposal wells (hauler_disposal_facilities).
  INVALID_DISPOSAL_WELL_ID: {
    httpStatus: 400,
    userMessage: 'That disposal well is unknown, inactive, or not authorized for your company.',
  },

  // === Phase 26 / Task 26.5 additions (DEC-147 #3, #5, #10) ===

  // Task 26.5 — create/patch hauler-location supplied a site_type_id that is
  // unknown, disabled in the platform catalog, or not enabled for this tenant
  // (per tenant_site_type_settings). Same tenant/enabled semantics the
  // Site Type picker enforces client-side via GET /v1/site-types.
  INVALID_SITE_TYPE_ID: {
    httpStatus: 400,
    userMessage: 'That site type is not available for this tenant.',
  },

  // Phase 15 (DEC-148/149) operator self-registration codes (OPERATOR_REGISTRATION_*,
  // MAGIC_LINK_TOKEN_*) and PR 1's OPERATOR_SELF_SIGNUP_CLOSED went with the self-signup and
  // approval-queue routes in OPP PR 13a.

  // The createNewOperator name/slug conflicts with an existing operator tenant.
  OPERATOR_TENANT_ALREADY_EXISTS: {
    httpStatus: 409,
    userMessage: 'An operator with that name already exists.',
  },
  // The operatorTenantId provided does not exist or is not an OPERATOR tenant.
  OPERATOR_TENANT_NOT_FOUND: {
    httpStatus: 404,
    userMessage: 'That operator was not found.',
  },
  // Cognito user creation (AdminCreateUser / AdminSetUserPassword) failed.
  // The registration row has NOT been updated — the admin can retry.
  COGNITO_USER_CREATION_FAILED: {
    httpStatus: 500,
    userMessage: 'Could not create the user account. Please try again.',
  },
  // Quick 260814-k6a — POST /v1/users: the email or phone already belongs to a
  // user. Both columns are GLOBALLY unique, so this is a global collision, not a
  // per-tenant one: the same person cannot be created twice across tenants.
  // Quick 260814-mh2 — AdminUpdateUserAttributes failed while syncing a phone
  // change. The DB write is rolled back, so Postgres and Cognito stay in agreement.
  COGNITO_USER_UPDATE_FAILED: {
    httpStatus: 500,
    userMessage: 'Could not update the account. Please try again.',
  },
  // Quick 260827-pwd — POST /v1/auth/change-password. Cognito's own wording is
  // unusable for both of these: NotAuthorizedException says "Incorrect username or
  // password" (the username is not in question — the user is already signed in),
  // and InvalidPasswordException says, literally, "Password did not conform with
  // policy: null". Verified against the live pool, not assumed. T-ERR-01 forbids
  // relaying Cognito sub-reasons anyway, so these carry our own wording.
  //
  // Separate codes rather than one, because the two are fixed by different
  // actions: retype what you already know, versus choose a different new password.
  // Collapsing them would put the user in a guessing loop.
  CURRENT_PASSWORD_INCORRECT: {
    httpStatus: 400,
    userMessage: 'That current password is not correct.',
  },
  NEW_PASSWORD_REJECTED: {
    httpStatus: 400,
    userMessage: 'That new password does not meet the requirements. Choose a different one.',
  },
  // Quick 260814-ud3 — team management guards.
  // Self-deactivation / self-demotion is a trivially self-inflicted lockout: the
  // last admin removing their own MANAGE_USERS leaves a tenant nobody can
  // administer, recoverable only by Paelode via direct DB access.
  CANNOT_MODIFY_SELF: {
    httpStatus: 409,
    userMessage: 'You cannot change your own role or deactivate your own account.',
  },
  // Drivers authenticate by SMS OTP and have a synthesized, undeliverable email;
  // office users authenticate by email + password. Flipping the role alone would
  // leave a "dispatcher" with no password and an address that cannot receive one.
  ROLE_CHANGE_CROSSES_LOGIN_TYPE: {
    httpStatus: 409,
    userMessage:
      'A driver cannot be changed into an office user, or the reverse. Create a new account instead.',
  },
  DRIVER_HAS_NO_PASSWORD: {
    httpStatus: 409,
    userMessage: 'Drivers sign in with a texted code — there is no password to reset.',
  },
  USER_ALREADY_EXISTS: {
    httpStatus: 409,
    userMessage: 'Someone with that phone number or email already has an account.',
  },
  // Phase 20 / Task 20.6e (DEC-252) — POST /v1/users/:id/invite refused because
  // the person already has a login (`users.cognito_sub` is set).
  //
  // Distinct from USER_ALREADY_EXISTS on purpose: that one means "this identity
  // collides with somebody else's", and the admin's next move is to pick a
  // different phone or email. This one means "this is the right person and they
  // are already set up", and the next move is Resend, not Invite. One message
  // for both would send the admin after the wrong fix.
  USER_ALREADY_INVITED: {
    httpStatus: 409,
    userMessage: 'This person already has a login. Use Resend if they need a new invitation.',
  },
  // SES send failed. This is best-effort — the approval/rejection is committed
  // to the database regardless; the email is not retried automatically.
  SES_SEND_FAILED: {
    httpStatus: 500,
    userMessage: 'Could not send the notification email.',
  },
  // --- Phase 15 / Task 15.4 additions (DEC-153) — portal tier + platform_operator link ---
  // The platform_operator_id passed to approve.ts was not found in platform_operators,
  // is inactive, or is merged away (merged_into_id IS NOT NULL).
  PLATFORM_OPERATOR_NOT_FOUND: {
    httpStatus: 404,
    userMessage: 'Platform operator not found.',
  },
  // (TIER_ACCESS_DENIED went with the operator tier guard in OPP PR 13a: operator plans and
  // FEATURE_NOT_ENABLED replaced it.)
  // §58 — a HAULER feature that is off for the caller's tenant (its template default or a
  // Paelode override). Returned by featureGuardPlugin for config.requiredFeature. The
  // message is §58's standard gated-feature text: no tier names, no pricing, no upgrade path.
  FEATURE_NOT_ENABLED: {
    httpStatus: 403,
    userMessage:
      'This feature is not available on your current plan. Contact Paelode support for more information.',
  },
  // §58 PR 4 — the hauler is on the EXPRESS plan: one customer (hauler_plans.express_operator_id).
  // New customers, sites, wells and jobs for any other customer are refused (DB trigger
  // hauler_plan_express_customer, migration 0195, and the API pre-checks). Existing work for
  // other customers carries on. Same no-tier-names rule as FEATURE_NOT_ENABLED.
  EXPRESS_CUSTOMER_ONLY: {
    httpStatus: 403,
    userMessage:
      'Your current plan covers one customer, so this is only available for that customer. Contact Paelode support for more information.',
  },
  // Operator portal rework PR 7 (0214) — the company's tank records and routes.
  OPERATOR_TANK_NOT_VISIBLE: {
    httpStatus: 422,
    userMessage:
      'That tank is not one of your haulers’ tanks for your company. Ask Paelode if a hauler is missing.',
  },
  OPERATOR_TANK_ALREADY_LINKED: {
    httpStatus: 409,
    userMessage: 'That hauler’s tank is already linked to one of your tanks. Unlink it first.',
  },
  // #918 review MED-1 — the hauler's tank matches one of the company's DEACTIVATED tanks; the
  // admin decides: reactivate that tank, or create a new one.
  OPERATOR_TANK_MATCHES_INACTIVE: {
    httpStatus: 409,
    userMessage:
      'That tank matches one of your deactivated tanks. Reactivate it, or choose to create a new tank.',
  },
  // #918 review LOW-1 — a deactivated route can be emptied but takes no new wells, tanks or foremen.
  OPERATOR_ROUTE_INACTIVE: {
    httpStatus: 409,
    userMessage: 'That route is deactivated. Reactivate it first.',
  },
  OPERATOR_TANK_NAME_TAKEN: {
    httpStatus: 409,
    // 13.8 (D-22): a tank's name is unique per placement (well, site or battery), not
    // company-wide; the database says which ("at this well / site / battery").
    userMessage: 'Another active tank on this well, site or battery already has that name.',
  },
  // 13.8 (D-20): the producer tank ID is unique across the company among active tanks.
  OPERATOR_TANK_PRODUCER_ID_TAKEN: {
    httpStatus: 409,
    userMessage: 'Another active tank already has this producer tank ID.',
  },
  // 13.8 review L3: an ID or TAG match on another well, site or battery is linked only when the
  // admin ticks "same tank" (operator_tank_confirm's accept list).
  OPERATOR_TANK_MATCH_PLACE_DIFFERS: {
    httpStatus: 409,
    userMessage:
      "That tank's match is on another well, site or battery. Tick 'same tank' to link it anyway.",
  },
  // Tank registry PR 1 (0245).
  OPERATOR_WELL_NOT_OWNED: {
    httpStatus: 400,
    userMessage: 'That well is not one of your company’s wells.',
  },
  OPERATOR_BATTERY_NAME_TAKEN: {
    httpStatus: 409,
    userMessage: 'Another of your batteries already has that name.',
  },
  OPERATOR_BATTERY_WELL_TAKEN: {
    httpStatus: 409,
    userMessage: 'That well is already on another of your batteries.',
  },
  OPERATOR_TANK_TAG_TAKEN: {
    httpStatus: 409,
    userMessage: 'Another of your tanks already has that tag in that system.',
  },
  OPERATOR_ROUTE_NAME_TAKEN: {
    httpStatus: 409,
    userMessage: 'Another active route already has that name.',
  },
  // A well, tank or person given to a route is not this company's (checked in SQL, §4.4).
  OPERATOR_COMPANY_SCOPE_VIOLATION: {
    httpStatus: 422,
    userMessage: 'That isn’t one of your company’s wells, tanks or foremen.',
  },
  // 0205 — two customers of one company would print the same ticket code (their HaulIds would
  // collide). The field reason names the customer that already uses it.
  TICKET_PREFIX_TAKEN: {
    httpStatus: 409,
    userMessage:
      'Another customer of this company already prints that code on its tickets. Choose a different code, or change the other customer’s code first.',
  },
  // 0205 — a Paelode abbreviation change or operator merge would give two customers of a hauler
  // the same ticket code. The field reason names the hauler and the customer.
  PLATFORM_ABBREVIATION_COLLIDES: {
    httpStatus: 409,
    userMessage:
      'That would give two customers of a hauler the same ticket code. See the collisions list on the Abbreviations page, choose another code, or have the hauler change its customer’s code first.',
  },
  // Operator dedupe (Session 1's pre-review MED-2): a merge or its preview waited too long for
  // rows that live work (driver sync, an invoice run) holds, or lost a deadlock. Nothing changed.
  OPERATOR_MERGE_BUSY: {
    httpStatus: 409,
    userMessage: 'Those operators are busy right now. Try again in a moment.',
  },
  // Operator dedupe (MED-3): the merge's warnings (a wrong-way merge) differ from the ones the
  // admin acknowledged, e.g. haulers moved since the preview. `fields` lists the current codes.
  OPERATOR_MERGE_WARNINGS_CHANGED: {
    httpStatus: 409,
    userMessage: 'Something about these operators changed since the preview. Review it again.',
  },
  // 0247: the pair already has a live "not a duplicate" ruling (either order).
  OPERATOR_DUPLICATE_ALREADY_DISMISSED: {
    httpStatus: 409,
    userMessage: 'That pair is already ruled not a duplicate.',
  },
  // OPP PR 6 (0212) — adding, importing or re-enabling operator users would pass the user limit
  // Paelode set on the company's plan (operator_plans.user_seat_limit). Nobody is created or
  // re-enabled. Only Paelode admins reach these routes, and only they can raise the limit.
  SEAT_LIMIT_REACHED: {
    httpStatus: 409,
    userMessage:
      'This company has reached its user limit. Raise the limit on its plan, or suspend someone first.',
  },
  // The operator tenant has not been linked to a platform_operators row (platform_operator_id IS NULL).
  // Returned by portal endpoints before any data query. 412 Precondition Failed — the link
  // is a required precondition for all portal data operations (not an authz failure).
  OPERATOR_PORTAL_NOT_LINKED: {
    httpStatus: 412,
    userMessage: 'This account is not linked to a platform operator yet. Contact support.',
  },
  // Standalone-well pickup (quick 260717-kme). The chosen well's platform operator
  // has no matching hauler_operators (customer) row for this tenant — so the job's
  // hauler_operator / HaulId abbreviation cannot be resolved. Deliberately NOT
  // auto-provisioned: hauler_operators is billing-relevant, so the dispatcher must
  // add the customer explicitly. Also returned when the well has no operator at all.
  OPERATOR_NOT_IN_CATALOG: {
    httpStatus: 422,
    userMessage:
      "This well's operator isn't in your customer list. Add this operator to your customers first.",
  },

  // === Phase 15 followup / DEC-154 additions — public-endpoint protection ===

  // The caller exceeded the rate-limit window for this endpoint. `refCode`
  // remains generic; the enclosing 429 response's `Retry-After` header carries
  // the reset delta (RFC 6585). The Pino log line
  // (event: 'rate_limit_denied') carries the specific key that tripped.
  RATE_LIMIT_EXCEEDED: {
    httpStatus: 429,
    userMessage: 'Too many attempts. Try again shortly.',
  },
  // OPEN-040 — the two Fastify transport-level rejections that had no catalog code,
  // so the error handler reported both as INTERNAL_ERROR 500. Telling a caller the
  // server broke when their upload was too large, or their content-type unsupported,
  // sends them to retry something that can never succeed.
  PAYLOAD_TOO_LARGE: {
    httpStatus: 413,
    userMessage: 'That request was too large.',
  },
  UNSUPPORTED_MEDIA_TYPE: {
    httpStatus: 415,
    userMessage: 'That content type is not supported.',
  },
  // OPEN-127 / DEC-341 — a reference-data pull asked for the whole set at once (no
  // ?limit=, as every app build before DEC-341 does) and the set is past that endpoint's
  // ceiling. Refused rather than cut: a partial list reads as the whole list on the phone,
  // and for job-type customer scoping that LOOSENS a restriction. Current builds page.
  REFERENCE_DATA_TOO_LARGE: {
    httpStatus: 422,
    userMessage: 'This list is too large to download in one go. Please update the app.',
  },
  // OPEN-133 / DEC-350 — the job already has a saved run ticket and this one is different.
  // The first capture wins while the job is open; a correction is accepted only after
  // review sends the ticket back (or returns a forced one to the stuck list). Only the online capture POST answers with this: the sync
  // receiver parks the push as DUPLICATE_CAPTURE instead, so the phone stays green.
  CAPTURE_ALREADY_RECORDED: {
    httpStatus: 409,
    userMessage:
      'A run ticket is already recorded for this job. Ask dispatch to send it back if it needs a correction.',
  },
  // OPEN-157 — the board or Job Detail tried to move a started job (its driver's queued work
  // would then be refused) or change the driver on a finished ticket. details.fields[0].reason
  // says which: job_started / job_finished.
  JOB_NOT_REASSIGNABLE: {
    httpStatus: 409,
    userMessage:
      "This job has started or finished, so it stays with its driver. If they can't finish it, send it to review or cancel it.",
  },
  // DEC-357 / OPEN-144 — approve refused: a second, different run ticket for this job is
  // parked (DUPLICATE_CAPTURE) and nobody has decided between the two yet.
  PARKED_CAPTURE_UNRESOLVED: {
    httpStatus: 409,
    userMessage:
      'A second run ticket is parked on this job. Keep the saved ticket or use the parked one before approving.',
  },
  // OPEN-169 addendum — Send Back is off for this hauler (tenant_inreview_settings.allow_send_back).
  // The reviewer fixes the ticket in review instead.
  SEND_BACK_DISABLED: {
    httpStatus: 409,
    userMessage:
      'Send Back is off for this company. Fix it here instead: record the actual disposal, correct the volume, or record that no disposal happened. Billing can correct the billed lines.',
  },
  // OPEN-169 addendum — turning Send Back on is refused until the phone app has a correction
  // flow (DRIVER_CORRECTION_FLOW_AVAILABLE).
  SEND_BACK_NEEDS_PHONE_UPDATE: {
    httpStatus: 409,
    userMessage: 'Driver corrections need the phone app update. Fix tickets in review instead.',
  },
  // DEC-357 — keep / dismiss on a parked item someone already resolved (or a stale screen).
  SYNC_CONFLICT_NOT_OPEN: {
    httpStatus: 409,
    userMessage: 'This parked item has already been resolved. Refresh to see the decision.',
  },
  // DEC-357 — keep (or use) on a parked item that is not a duplicate run ticket, or dismiss
  // on one that is: a duplicate needs a decision between the two tickets, not a dismissal.
  SYNC_CONFLICT_KIND_MISMATCH: {
    httpStatus: 409,
    userMessage: 'That action does not apply to this kind of parked item.',
  },
  // DEC-357 / OPEN-144 PR 2 — "Use the parked ticket" is allowed only while the ticket is in
  // review and nothing has been billed for it (after approval the fix is a billing correction).
  PARKED_APPLY_NOT_IN_REVIEW: {
    httpStatus: 409,
    userMessage:
      'The parked ticket can only be used while this ticket is in review. Keep the saved ticket, or correct it in billing.',
  },
  // DEC-357 — the reviewer edited the volume against the saved ticket's numbers.
  PARKED_APPLY_VOLUME_EDITED: {
    httpStatus: 409,
    userMessage: 'Revert the volume edit on this ticket before using the parked ticket.',
  },
  // DEC-357 — the saved ticket was corrected after the second one was parked.
  PARKED_APPLY_SAVED_CHANGED: {
    httpStatus: 409,
    userMessage:
      'The saved ticket was corrected after this one was parked. Keep the saved ticket, or send it back.',
  },
  // OPEN-157 B follow-up — "Use the parked ticket" on a run ticket from a driver the job was
  // taken back from, once the job already has a run ticket (the new driver captured, or the
  // old one did before the take-back). Use is for a job with no ticket yet.
  TAKEN_BACK_APPLY_CAPTURED: {
    httpStatus: 409,
    userMessage:
      'This job already has a run ticket, so the parked one cannot be used. Dismiss it, or fix the saved ticket in review.',
  },
  // OPEN-147 — a service charge with this code already exists for the tenant
  // (service_charges_tenant_code_idx). It used to surface as a 500. A deactivated
  // charge still holds its code, so the usual fix is to reactivate that one.
  SERVICE_CHARGE_CODE_EXISTS: {
    httpStatus: 409,
    userMessage:
      'A service charge with that code already exists. Use a different code, or reactivate the existing one.',
  },
  // 86bc83kk9 — invoice lifecycle. Sibling of JOB_TRANSITION_FORBIDDEN: the request was
  // well-formed and permitted, but the invoice is not in a state that allows the action
  // (e.g. marking a DRAFT paid, or sending one that is already VOID). 409, not 400 —
  // nothing about the request needs fixing, only the invoice's state.
  INVOICE_TRANSITION_FORBIDDEN: {
    httpStatus: 409,
    userMessage: 'This invoice cannot move to that status from its current state.',
  },
  // #50 PR D — the hauler invoice PDF couldn't be made: the renderer isn't configured on
  // this server, failed, or the document is over its size limit. The invoice is fine.
  INVOICE_PDF_UNAVAILABLE: {
    httpStatus: 503,
    userMessage: 'The invoice PDF can’t be made right now. Try again in a few minutes.',
  },
  // Operator Reports (0241) — the portal's run-ticket PDF couldn't be made: the renderer isn't
  // configured on this server, or it failed. The ticket is fine; the page still shows it.
  OPERATOR_TICKET_PDF_UNAVAILABLE: {
    httpStatus: 503,
    userMessage: 'The ticket PDF can’t be made right now. Try again in a few minutes.',
  },
  // Operator Reports (0241) — an export would carry more than PORTAL_EXPORT_MAX_ROWS tickets.
  // Nothing is cut off silently: the operator narrows the dates and exports again.
  OPERATOR_REPORT_TOO_LARGE: {
    httpStatus: 422,
    userMessage: 'That export has too many tickets. Choose a shorter date range and try again.',
  },
  // Operator statement (0256) — the PDF renderer is not configured or the render failed.
  OPERATOR_STATEMENT_UNAVAILABLE: {
    httpStatus: 503,
    userMessage: 'The statement can’t be made right now. Try again in a few minutes.',
  },
  // DEC-356 (#50) — billing's edit of an approved ticket (PATCH /v1/billing/tickets/:jobId).
  // A line the edit touches is already on an invoice (DRAFT included); the way back is a
  // void, which returns the work to the Pipeline (DEC-322).
  TICKET_ON_INVOICE: {
    httpStatus: 409,
    userMessage: 'This ticket is on an invoice. Void the invoice to edit it.',
  },
  // DEC-356 — the stale-save guard: a value the form loaded changed before it was saved.
  TICKET_CHANGED: {
    httpStatus: 409,
    userMessage: 'This ticket changed since you opened it. Reload and try again.',
  },
  // DEC-356 — the ticket isn't waiting to be invoiced, the field doesn't apply to it, or
  // (OPEN-159) it's a volume edit on a ticket with more than one capture.
  TICKET_NOT_EDITABLE: {
    httpStatus: 409,
    userMessage: 'That change cannot be made to this ticket.',
  },
  // Request body was missing the `captchaToken` field required by the public
  // endpoint. Distinct from VALIDATION_FAILED so observability can distinguish
  // bot traffic ("did not even try") from human misuse.
  CAPTCHA_REQUIRED: {
    httpStatus: 400,
    userMessage: 'Please complete the verification challenge.',
  },
  // Cloudflare Turnstile reported `success: false` for the submitted token
  // (bad token, expired, replayed, or hostname mismatch). Same treatment as
  // missing token from the client's perspective — retry with a fresh widget.
  CAPTCHA_VERIFICATION_FAILED: {
    httpStatus: 400,
    userMessage: 'Verification failed. Please try again.',
  },

  // === Phase 16 / Task 16.1 additions (DEC-156) — disposal portal self-registration ===

  // Post-Zod semantic failure for disposal registrant email addresses (reserved
  // for disposable-domain blocklisting or similar checks in a future 16.x task).
  // Zod's .email() validator catches malformed email syntax BEFORE this code.
  DISPOSAL_REGISTRATION_INVALID_EMAIL: {
    httpStatus: 400,
    userMessage: 'That email address is not valid.',
  },
  // Reserved for post-Zod license-format checks — e.g. OK OCC permit format
  // regex enforced when state='OK' + licenseNumber provided. Not wired in 16.1;
  // present in the catalog so 16.2+ can throw it without a catalog migration.
  DISPOSAL_REGISTRATION_INVALID_LICENSE_FORMAT: {
    httpStatus: 400,
    userMessage: 'That license number is not in a recognized format.',
  },
  // Reserved for post-Zod state checks. Zod's .enum() already rejects out-of-set
  // values as VALIDATION_FAILED; this code covers future cross-field checks
  // (e.g. licenseNumber provided but state incompatible with license format).
  DISPOSAL_REGISTRATION_INVALID_STATE: {
    httpStatus: 400,
    userMessage: 'That state is not supported.',
  },
  // Disposal-portal magic link has passed its 15-minute TTL. Client should call
  // the issue endpoint again to request a fresh link. (Its operator-portal twin,
  // MAGIC_LINK_TOKEN_EXPIRED, went with operator self-signup in OPP PR 13a.)
  DISPOSAL_MAGIC_LINK_TOKEN_EXPIRED: {
    httpStatus: 410,
    userMessage: 'This link expired. Request a new one.',
  },
  // Disposal-portal magic link is unknown, already consumed, or the
  // registration has advanced beyond PENDING_EMAIL_VERIFY. Intentionally does
  // NOT distinguish these cases — distinguishing them would reveal whether a
  // token was ever valid (mirror DEC-148 §4).
  DISPOSAL_MAGIC_LINK_TOKEN_INVALID_OR_CONSUMED: {
    httpStatus: 400,
    userMessage: 'This link is no longer valid. Request a new one.',
  },

  // === Phase 16 / Task 16.2 additions (DEC-157) — disposal-registration approval flow ===

  // Admin tried to load/approve/reject a disposal registration that does not exist.
  DISPOSAL_REGISTRATION_NOT_FOUND: {
    httpStatus: 404,
    userMessage: 'That registration was not found.',
  },
  // Admin tried to approve or reject a disposal registration that has already
  // been processed (status APPROVED or REJECTED).
  DISPOSAL_REGISTRATION_ALREADY_PROCESSED: {
    httpStatus: 409,
    userMessage: 'That registration has already been processed.',
  },
  // Admin tried to approve or reject a disposal registration that is still in
  // PENDING_EMAIL_VERIFY (applicant has not clicked the magic link yet).
  DISPOSAL_REGISTRATION_NOT_EMAIL_VERIFIED: {
    httpStatus: 409,
    userMessage: 'The applicant has not verified their email yet.',
  },
  // Admin tried to approve a registration with license_match_status IN
  // (UNMATCHED, NOT_PROVIDED) without providing a manualVerificationNote.
  // The note is required so the audit trail records the human verification
  // reasoning (§49 + DEC-157).
  DISPOSAL_REGISTRATION_MANUAL_VERIFICATION_NOTE_REQUIRED: {
    httpStatus: 400,
    userMessage:
      'A verification note is required when approving a registration with no license match.',
  },
  // The createNewDisposalCompany name/slug conflicts with an existing tenant.
  DISPOSAL_TENANT_ALREADY_EXISTS: {
    httpStatus: 409,
    userMessage: 'A disposal company with that name already exists.',
  },
  // The disposalCompanyId provided does not exist, or its parent tenant is not
  // a DISPOSAL tenant type.
  DISPOSAL_COMPANY_NOT_FOUND: {
    httpStatus: 404,
    userMessage: 'That disposal company was not found.',
  },
  // Cognito user creation (AdminCreateUser / AdminSetUserPassword) failed for
  // the disposal pool. The registration row has NOT been updated — the admin
  // can retry.
  COGNITO_DISPOSAL_USER_CREATION_FAILED: {
    httpStatus: 500,
    userMessage: 'Could not create the user account. Please try again.',
  },

  // === Phase 16 / Task 16.3 additions (DEC-158) — disposal-company activation ===

  // Admin tried to activate a disposal_company that is already ACTIVE.
  // Opinionated 409 (not 200 unchanged) — activation is an event with an
  // audit-worthy signal; retries should be visible per DEC-158.
  DISPOSAL_COMPANY_ALREADY_ACTIVE: {
    httpStatus: 409,
    userMessage: 'This disposal company is already active.',
  },
  // Admin tried to toggle modules on a company whose portal_status is not
  // ACTIVE (still PAYMENT_PENDING, or SUSPENDED). Activate first, then toggle.
  DISPOSAL_COMPANY_NOT_ACTIVE: {
    httpStatus: 409,
    userMessage: 'Activate this disposal company before toggling modules.',
  },
  // Admin tried to activate a SUSPENDED company via the activate endpoint.
  // Reactivation from SUSPENDED is its own endpoint, /:id/reactivate (0234).
  DISPOSAL_COMPANY_NOT_ACTIVATABLE: {
    httpStatus: 409,
    userMessage: 'This disposal company cannot be activated from its current status.',
  },
  // === Disposal company suspend / reactivate (migration 0234) ===

  // Suspend asked of a company that is already SUSPENDED.
  DISPOSAL_COMPANY_ALREADY_SUSPENDED: {
    httpStatus: 409,
    userMessage: 'This disposal company is already suspended.',
  },
  // Suspend asked of a PAYMENT_PENDING company: its portal never opened, so there is
  // nothing to stop.
  DISPOSAL_COMPANY_NOT_SUSPENDABLE: {
    httpStatus: 409,
    userMessage: 'Only an active disposal company can be suspended.',
  },
  // Reactivate asked of a company that is not SUSPENDED (activate a PAYMENT_PENDING one).
  DISPOSAL_COMPANY_NOT_SUSPENDED: {
    httpStatus: 409,
    userMessage: 'This disposal company is not suspended.',
  },
  // Module toggle request referenced a module name that is not in the 10-name
  // §49 allowlist. In practice Zod .strict() rejects unknown keys at parse
  // time — this code is the belt-and-braces fallback raised by
  // platform_set_disposal_modules if a bad key somehow slipped through.
  DISPOSAL_MODULE_UNKNOWN: {
    httpStatus: 400,
    userMessage: 'One or more requested modules are not recognized.',
  },

  // === Phase 16 / Task 16.4 additions (DEC-161) — disposal daily email recipients ===

  // DISPOSAL_ADMIN referenced a recipient id that does not exist in the
  // caller's disposal_company (RLS-scoped). 404 rather than 403 — a legitimate
  // race (recipient just removed by another admin) should surface as "gone",
  // not as an authz failure.
  DISPOSAL_EMAIL_RECIPIENT_NOT_FOUND: {
    httpStatus: 404,
    userMessage: 'That recipient was not found.',
  },
  // DISPOSAL_ADMIN tried to add an email + scope combination that is already
  // an active recipient (partial UNIQUE index hit). 409 opinionated — the
  // admin should see a duplicate signal so they know the address is already
  // subscribed.
  DISPOSAL_EMAIL_RECIPIENT_ALREADY_EXISTS: {
    httpStatus: 409,
    userMessage: 'That email is already subscribed for this scope.',
  },
  // DISPOSAL_ADMIN supplied a facilityId that does not belong to the caller's
  // disposal_company. 400 rather than 404 — this is a client bug (the picker
  // should not have offered a foreign facility), not a race.
  DISPOSAL_EMAIL_RECIPIENT_FACILITY_MISMATCH: {
    httpStatus: 400,
    userMessage: 'That facility does not belong to your company.',
  },
  // Unsubscribe endpoint received a token whose HMAC signature does not match
  // (forged, corrupt, or from a different secret). 400 — indistinguishable to
  // the user from a truncated URL; the deep reason lives in the Pino log.
  DISPOSAL_UNSUBSCRIBE_TOKEN_INVALID: {
    httpStatus: 400,
    userMessage: 'This unsubscribe link is invalid.',
  },
  // Unsubscribe token has passed its 30-day TTL. Distinct from INVALID so
  // the user sees a helpful "old link" message rather than a scary "bad link"
  // one. 410 Gone — the resource (token grant) expired.
  DISPOSAL_UNSUBSCRIBE_TOKEN_EXPIRED: {
    httpStatus: 410,
    userMessage: 'This unsubscribe link expired.',
  },

  // === Phase 16 / Task 16.5 additions (DEC-162) — disposal custom reports ===

  // DISPOSAL_ADMIN referenced a template id that does not exist in the
  // caller's disposal_company (RLS-scoped). 404 rather than 403 — a
  // legitimate race (template just archived by another admin) surfaces as
  // "gone", not as an authz failure.
  DISPOSAL_REPORT_TEMPLATE_NOT_FOUND: {
    httpStatus: 404,
    userMessage: 'That report template was not found.',
  },
  // The `fields` array on a create/update template request has a duplicate
  // `order` value, an unknown `key`, or is otherwise semantically invalid
  // (Zod .enum() catches unknown keys at parse time — this code is the
  // route-layer belt-and-braces + duplicate-order signal).
  DISPOSAL_REPORT_TEMPLATE_INVALID_FIELDS: {
    httpStatus: 400,
    userMessage: 'One or more report columns are invalid.',
  },
  // The template's facilityId does not belong to the caller's
  // disposal_company. 400 rather than 404 — client bug (the picker should
  // not have offered a foreign facility), not a race.
  DISPOSAL_REPORT_TEMPLATE_FACILITY_MISMATCH: {
    httpStatus: 400,
    userMessage: 'That facility does not belong to your company.',
  },
  // Report-run id does not exist in the caller's disposal_company
  // (RLS-scoped). Symmetric with template-not-found.
  DISPOSAL_REPORT_RUN_NOT_FOUND: {
    httpStatus: 404,
    userMessage: 'That report run was not found.',
  },
  // Generate endpoint received a template whose format = 'PDF'. Shipped as
  // 501 NOT_IMPLEMENTED (rather than 400 validation) so the client knows
  // this is a temporary infrastructure gap (Phase 17 / 19 shared Puppeteer
  // infra ships PDF). Message points to the future release.
  DISPOSAL_REPORT_PDF_NOT_IMPLEMENTED: {
    httpStatus: 501,
    userMessage: 'PDF reports are not available yet. Use CSV or Excel for now.',
  },
  // Generate endpoint received a date range longer than 90 days. Enforced
  // at the API layer (not the DB) so runaway report queries never queue.
  DISPOSAL_REPORT_DATE_RANGE_TOO_LONG: {
    httpStatus: 400,
    userMessage: 'Report date range cannot exceed 90 days.',
  },
  // DISPOSAL_ADMIN tried to add an email + scope that is already an active
  // report recipient (partial UNIQUE index hit). 409 opinionated — the
  // admin should see a duplicate signal so they know the address is
  // already subscribed for reports.
  DISPOSAL_REPORT_RECIPIENT_ALREADY_EXISTS: {
    httpStatus: 409,
    userMessage: 'That email is already subscribed for this scope.',
  },
  // DISPOSAL_ADMIN supplied a report-recipient facilityId that does not
  // belong to the caller's disposal_company. 400 rather than 404 — client
  // bug (the picker should not have offered a foreign facility), not a race.
  DISPOSAL_REPORT_RECIPIENT_FACILITY_MISMATCH: {
    httpStatus: 400,
    userMessage: 'That facility does not belong to your company.',
  },
  // Report unsubscribe endpoint received a token whose HMAC signature does
  // not match. 400 — indistinguishable to the user from a truncated URL;
  // the deep reason lives in the Pino log. Distinct from
  // DISPOSAL_UNSUBSCRIBE_TOKEN_INVALID (16.4 daily-email) so observability
  // can tell the two feature surfaces apart.
  DISPOSAL_REPORT_UNSUBSCRIBE_TOKEN_INVALID: {
    httpStatus: 400,
    userMessage: 'This unsubscribe link is invalid.',
  },
  // Report unsubscribe token has passed its 30-day TTL. Distinct from
  // INVALID so the user sees a helpful "old link" message. 410 Gone.
  DISPOSAL_REPORT_UNSUBSCRIBE_TOKEN_EXPIRED: {
    httpStatus: 410,
    userMessage: 'This unsubscribe link expired.',
  },
  // Generate endpoint fired against a disposal_company whose
  // module_custom_reports = false. 409 (portal state conflict) rather than
  // 403 (permission) so a DISPOSAL_ADMIN who's toggling a module off/on
  // gets a clear "module not active" signal instead of an authz message.
  DISPOSAL_MODULE_NOT_ACTIVE: {
    httpStatus: 409,
    userMessage: 'This feature is not enabled for your company yet.',
  },

  // === Phase 16 / Task 16.6 additions (DEC-163) — disposal BI panels MVP ===

  // BI panel endpoint hit but the specific module boolean on
  // disposal_companies is false OR portal_status is PAYMENT_PENDING /
  // SUSPENDED. Distinguishes which module in the Pino log line via
  // `event: 'bi_module_gate'` + `module: 'volume_trends' | 'acceptance_by_site' | 'data_export'`.
  // 409 (portal state conflict) rather than 403 so the DISPOSAL_ADMIN sees
  // a clear "not enabled" signal, not an authz message. Distinct from
  // DISPOSAL_MODULE_NOT_ACTIVE (16.5 custom reports) so a future admin
  // sub-role split can gate BI + reports independently.
  DISPOSAL_BI_MODULE_NOT_ACTIVE: {
    httpStatus: 409,
    userMessage: 'This BI panel is not enabled for your company yet.',
  },
  // data-export received a date range longer than 365 days. Enforced at the
  // API layer to keep runaway export queries out of the SECURITY DEFINER
  // aggregation. Distinct from DISPOSAL_REPORT_DATE_RANGE_TOO_LONG (16.5
  // has a 90-day cap on scheduled reports) so observability can tell the
  // two feature surfaces apart.
  DISPOSAL_BI_DATE_RANGE_TOO_LARGE: {
    httpStatus: 400,
    userMessage: 'Export date range cannot exceed 365 days.',
  },

  // === Phase 09.1 additions ===

  // JOBTYPE-12 / D-01 — PATCH /v1/job-types/:id included a STRUCTURAL field
  // (ticketShape / tankCountMode / tankCountMax) with a changed value but the
  // body did not carry acknowledgeVersion:true. Structural edits fork a new
  // version row (D-01/D-04) and must never mutate in place, so the client has
  // to acknowledge first. The 409 body carries the edit-impact payload.
  JOB_TYPE_VERSION_REQUIRED: {
    httpStatus: 409,
    userMessage: 'This change creates a new version of the job type. Confirm to continue.',
  },
  // JOBTYPE-01/02 — an operation targeted a job_types row whose superseded_at is
  // NOT NULL (an old version). Old versions are read-only history; edit the live
  // version of the family instead.
  JOB_TYPE_SUPERSEDED: {
    httpStatus: 409,
    userMessage: 'This job type version has been replaced. Reload and edit the current version.',
  },
  // JOBTYPE-04 — the capture body carried more tanks than the job type's
  // tank_count_mode / tank_count_max allow.
  TANK_COUNT_LIMIT_EXCEEDED: {
    httpStatus: 400,
    userMessage: 'This job type allows fewer tanks than were submitted.',
  },
  // D-13 — capture POST against a ticket_shape='LINE_ITEM' job type. 09.1 ships
  // the fork DISCRIMINATOR only; the Line-Item capture path and its Run/Stop/
  // LineItem tables land in the Supplier Tenant & Consumable Delivery phase
  // (ClickUp 86baxtwc8). Explicit 400 beats a silent half-written tank capture.
  LINE_ITEM_CAPTURE_NOT_SUPPORTED: {
    httpStatus: 400,
    userMessage: 'Line-item tickets are not yet supported for capture.',
  },
  // JOBTYPE-18 — the sync receiver's offline create-load mint (receive.ts)
  // resolved NO live job_types row for the pushed jobTypeCode (unknown code,
  // or every row sharing that code is superseded). Previously this fell
  // through to an opaque jobs.job_type_id NOT NULL constraint failure deep in
  // the sync transaction; this named rejection lets the mobile drain loop
  // surface a clear, permanent (non-retryable) error to the driver instead.
  JOB_TYPE_CODE_NOT_FOUND: {
    httpStatus: 400,
    userMessage: 'That job type is unknown or no longer available. Refresh and try again.',
  },

  // === Phase 12.1 additions ===

  // LINEITEM-05 / D-04 — capture-write.ts (Plan 12.1-02) rejects a line-item
  // capture BEFORE any write when the resolved product's
  // default_unit_rate_cents IS NULL. "Fail loud before any write" — never
  // silently bill $0, never strand a driver mid-ticket with a half-written
  // capture. Same posture as TANK_COUNT_LIMIT_EXCEEDED / INVALID_PRODUCT_ID.
  PRODUCT_RATE_NOT_CONFIGURED: {
    httpStatus: 400,
    userMessage: "This product isn't set up for billing yet — contact dispatch.",
  },

  // Deferred item 09.1-09 — a dispatcher tried to change a job's Job Type via
  // PATCH /v1/jobs/:id AFTER a run ticket (haul_captures row) already exists for
  // the job. The captured tank data was recorded against the OLD job type's
  // shape/requirements (DEC-024 snapshots capture_method onto haul_captures), so
  // re-pointing job_type_id would silently invalidate that record. The change is
  // refused server-side (the client mirrors this by rendering the Job Type field
  // read-only once a capture exists) — this is the load-bearing correctness rule;
  // there is no migrate/reconcile path. 409 (state conflict): the job's state (it
  // has a capture) forbids the mutation, not the request shape.
  //   Driver/end-user: the userMessage below.
  //   Client dev: branch on this code to keep the Job Type selector read-only.
  //   Server/support: the Pino log carries the jobId + attempted jobTypeId.
  JOB_TYPE_LOCKED_AFTER_CAPTURE: {
    httpStatus: 409,
    userMessage: "Can't change the job type after a ticket has been captured for this job.",
  },
  /**
   * Phase 20 / Task 20.7c (DEC-282) — the import commit is already claimed.
   *
   * The commit is async: one conditional UPDATE on `import_batches.commit_state`
   * is the whole concurrency gate, so of two simultaneous presses of Import one
   * gets the 202 and the other gets this.
   *
   * It means a writer is LIVE — QUEUED, or RUNNING with a lease that has not
   * lapsed — and nothing else. A finished import is not refused: re-running a
   * completed batch is a no-op by AC-5, because every row already carries an
   * outcome and resume skips it, so the idempotency does that job and this code
   * does not have to.
   */
  IMPORT_COMMIT_ALREADY_RUNNING: {
    httpStatus: 409,
    userMessage: 'This import is already running. Watch its progress for the result.',
  },

  // --- DEC-307 — the commit must say which kind of run it is ---
  //
  // `POST /v1/imports/:id/commit` used to treat an ABSENT `isDryRun` as "leave
  // the batch's stored flag alone". That is defensible in isolation and wrong in
  // practice: after a dry run the stored flag is true, so a second commit sent
  // with no flag re-runs as a DRY RUN and returns the same success-shaped counts
  // (`updated: 1`) while writing nothing. Measured on dev while walking a rate
  // import: the report said a rate had been set, `import_rows.outcome` was NULL,
  // no id-map row existed and the row's `updated_at` had not moved.
  //
  // So the flag is required, and its absence is named rather than folded into a
  // bare VALIDATION_FAILED — because the bare 400 is what made the original
  // mistake hard to see.
  IMPORT_DRY_RUN_FLAG_REQUIRED: {
    httpStatus: 400,
    userMessage:
      'Say whether this is a test run or the real import. Nothing was imported — send the request again with that choice.',
  },

  // --- Phase 18 / Task 18.1 — operator API credential lifecycle ---
  //
  // The tenant id submitted as `operatorTenantId` is not an OPERATOR tenant (or
  // does not exist at all). 404 rather than a field-level VALIDATION_FAILED: the
  // id is well-formed, it just does not name an operator.
  // Operator portal rework PR 9 (0213) — the operator's plan (OP_API) allows this many live API
  // keys, across all its haulers, and they are all in use. A field reason carries the limit.
  OPERATOR_API_KEY_LIMIT_REACHED: {
    httpStatus: 409,
    userMessage:
      'This company is using all the API keys its plan allows. Revoke one first, or ask Paelode to raise the limit.',
  },
  // Operator webhooks PR 1 (0239) — the company's plan (OP_WEBHOOKS) allows this many webhook
  // endpoints and they are all set up. A field reason carries the limit.
  OPERATOR_WEBHOOK_LIMIT_REACHED: {
    httpStatus: 409,
    userMessage:
      'This company has all the webhook endpoints its plan allows. Remove one first, or ask Paelode to raise the limit.',
  },
  // 13.11 (0263): MQTT gateways, provisioned by Paelode only (D-12).
  OPERATOR_MQTT_PAELODE_ONLY: {
    httpStatus: 403,
    userMessage: 'Only Paelode can add, rotate or revoke MQTT gateways.',
  },
  OPERATOR_MQTT_GATEWAY_LIMIT_REACHED: {
    httpStatus: 409,
    userMessage:
      'This company has all the MQTT gateways its plan allows. Revoke one first, or raise the limit.',
  },
  OPERATOR_MQTT_GATEWAY_NOT_FOUND: {
    httpStatus: 404,
    userMessage: 'That MQTT gateway was not found for this company.',
  },
  OPERATOR_MQTT_GATEWAY_NOT_LIVE: {
    httpStatus: 409,
    userMessage: 'That MQTT gateway is revoked or failed, so it cannot be changed.',
  },
  OPERATOR_MQTT_ROTATION_IN_PROGRESS: {
    httpStatus: 409,
    userMessage: 'A certificate rotation is already waiting. Finish it before starting another.',
  },
  OPERATOR_MQTT_NO_ROTATION: {
    httpStatus: 409,
    userMessage: 'That MQTT gateway is not being rotated.',
  },
  OPERATOR_MQTT_PROVISION_FAILED: {
    httpStatus: 502,
    userMessage:
      'AWS IoT did not finish setting up the gateway. Nothing was left active; try again.',
  },
  // Operator webhooks PR 1 (0239) — no endpoint with that id in the caller's company (or removed).
  OPERATOR_WEBHOOK_NOT_FOUND: {
    httpStatus: 404,
    userMessage: 'That webhook endpoint was not found.',
  },
  // Operator webhooks PR 1 (0239) — the URL is not one we will send to: not https, a port other
  // than 443, credentials in it, or a host that is (or resolves to) a private, loopback,
  // link-local or otherwise non-public address (services/webhook-url-guard.ts). The field reason
  // says which.
  OPERATOR_WEBHOOK_URL_REFUSED: {
    httpStatus: 422,
    userMessage:
      'That address can’t receive webhooks. Use an https:// URL on port 443 that is reachable from the internet.',
  },
  // Operator portal rework PR 9 (0213) — a hauler can no longer issue, rotate or revoke operator
  // API keys (Corbin, requirement 6): Paelode and the operator admin do.
  OPERATOR_API_KEYS_MANAGED_BY_PAELODE: {
    httpStatus: 403,
    userMessage: 'API keys are managed by Paelode and the operator. Contact Paelode to change one.',
  },
  OPERATOR_API_CREDENTIAL_OPERATOR_NOT_FOUND: {
    httpStatus: 404,
    userMessage: 'That operator could not be found.',
  },
  // The hauler has no active customer (hauler_operators) record for that
  // operator, so it has no relationship to issue a credential against. This is
  // the gate that stops a hauler minting API capacity for an operator it does
  // not haul for — DEC-239 gives the hauler issuance precisely because it owns
  // the commercial relationship, and this is what "owns" means mechanically.
  OPERATOR_API_CREDENTIAL_OPERATOR_NOT_SERVED: {
    httpStatus: 422,
    userMessage: 'Add that operator as a customer before issuing API access for them.',
  },
  // Rotation of a credential that has already been rotated. Rotating twice would
  // fork the chain and leave two successors both claiming the same predecessor,
  // so the caller is sent to the successor instead.
  OPERATOR_API_CREDENTIAL_ALREADY_ROTATED: {
    httpStatus: 409,
    userMessage: 'This key has already been replaced. Rotate its replacement instead.',
  },
  // Rotation of a credential that is revoked or already past its expiry. There is
  // nothing to carry forward: rotation hands a live integration a new secret,
  // and this one is not live.
  OPERATOR_API_CREDENTIAL_NOT_ACTIVE: {
    httpStatus: 409,
    userMessage: 'This key is no longer active. Issue a new one instead.',
  },
  // 13.9 (D-08, DEC-372, 0260) — a scope the key's kind cannot hold: a company-wide key holds only
  // tanks:read and readings:write, and a hauler key never holds readings:write. Refused by the
  // request schema first, and by 0260's definer and trigger in the database.
  OPERATOR_API_SCOPE_NOT_FOR_KIND: {
    httpStatus: 400,
    userMessage:
      'That scope is not available on this kind of key. A company-wide key can only read your tank list and send tank readings.',
  },
  // 13.10 (0261): POST /v1/operator-api/tank-readings, one per reading in `results[].error`
  // (the request itself still succeeds). The unmatched list stores the short reason.
  OPERATOR_API_READING_UNKNOWN_TANK_ID: {
    httpStatus: 400,
    userMessage: 'No active tank of your company has that tank ID.',
  },
  OPERATOR_API_READING_UNKNOWN_TAG: {
    httpStatus: 400,
    userMessage: 'No active tank of your company has that tag.',
  },
  OPERATOR_API_READING_UNKNOWN_ID: {
    httpStatus: 400,
    userMessage: 'No tank of your company has that id.',
  },
  OPERATOR_API_READING_TANK_INACTIVE: {
    httpStatus: 400,
    userMessage: 'That tank is no longer active, so it takes no readings.',
  },
  OPERATOR_API_READING_TANK_KEY_MISMATCH: {
    httpStatus: 409,
    userMessage: 'The tank ID and the tag or id in this reading name different tanks.',
  },
  OPERATOR_API_READING_CLOCK_AHEAD: {
    httpStatus: 400,
    userMessage: 'That reading is more than 5 minutes in the future. Check the sending clock.',
  },
  OPERATOR_API_READING_TOO_OLD: {
    httpStatus: 400,
    userMessage: 'That reading is more than 30 days old.',
  },
  OPERATOR_API_READING_OVER_CAPACITY: {
    httpStatus: 400,
    userMessage: 'That level is more than the tank holds.',
  },
  OPERATOR_API_READING_IDEMPOTENCY_KEY_REUSED: {
    httpStatus: 409,
    userMessage: 'That idempotencyKey was already used for a reading of another tank.',
  },

  // --- Phase 18 / Task 18.2 — operator public API authentication ---
  //
  // The credential authenticated, but its granted scopes do not include the one
  // this endpoint requires. Distinct from PERMISSION_DENIED on purpose: the
  // caller is a key, not a person, so "you don't have access to this action"
  // sends an integrator looking at a user's role when the fix is a scope on the
  // key their hauler issued.
  //
  // The MISSING SCOPE is not in this message. A static catalog string cannot
  // carry it, and the per-endpoint scope table is published documentation
  // (Task 18.10) rather than something to rediscover from a 403 — so the scope
  // the route wanted and the ones the key holds go to the server log and the
  // audit row (OPERATOR_API_SCOPE_DENIED), where support can read both.
  //
  // Every credential-level failure — absent header, malformed token, unknown
  // token, revoked, expired — deliberately collapses into UNAUTHENTICATED
  // instead of getting codes of its own: distinguishing "revoked" from "unknown"
  // would confirm to the holder of a leaked key that the key was ever real.
  OPERATOR_API_SCOPE_DENIED: {
    httpStatus: 403,
    userMessage: 'This API key is missing a scope required by this endpoint.',
  },
  // Operator portal PR 3 (0206, #904 review MED2) — the key is valid, but Paelode has not
  // confirmed the issuing hauler for this operator (operator_hauler_access), so the whole Operator
  // API refuses it: Paelode chooses which haulers each operator sees (Corbin). 403, not 401: the
  // key is fine, and rotating it would not help.
  OPERATOR_API_HAULER_NOT_CONFIRMED: {
    httpStatus: 403,
    userMessage:
      "Paelode has not confirmed this API key's hauler for your company yet. Contact Paelode support.",
  },
  // Operator portal PR 4 (0211, Session 2's review M3) — "Recompute now" while another recompute
  // (the nightly task or a second click) still runs. Refused at once rather than queued.
  SUGGESTION_RECOMPUTE_RUNNING: {
    httpStatus: 409,
    userMessage: 'A match-up recompute is already running. Try again in a minute.',
  },
  // Operator portal PR 4 (0211, Session 2's review M2) — confirming a suggestion for a pair Paelode
  // already decided by hand (access granted or revoked): the suggestion never overturns that.
  SUGGESTION_ALREADY_DECIDED: {
    httpStatus: 409,
    userMessage:
      'Hauler access for this operator was already set by hand, so this suggestion cannot change it.',
  },

  // --- Phase 18 / Task 18.3 — operator public API quota (DEC-300) ---
  //
  // The credential exhausted its MONTHLY call quota. Deliberately NOT
  // RATE_LIMIT_EXCEEDED, even though both are 429: a burst limit clears in
  // seconds and the right client response is to slow down, while a quota clears
  // at the next UTC month and no amount of backing off helps. An integrator told
  // "too many attempts, try again shortly" for an exhausted monthly quota will
  // build a retry loop that cannot ever succeed and will look, from their side,
  // like an outage. The `Retry-After` header carries the real delta in both
  // cases; this code is what lets a client tell the two situations apart without
  // parsing it.
  //
  // Which ceiling was hit (total vs the tighter write sub-ceiling) is in the
  // server log and the response's quota headers, not in this message — a static
  // catalog string cannot carry a number.
  OPERATOR_API_QUOTA_EXCEEDED: {
    httpStatus: 429,
    userMessage: 'This API key has used its quota for the month.',
  },

  // --- Phase 18 / Task 18.6 — POST /v1/operator-api/haul-requests ---
  //
  // Idempotency is mandatory on the write (18-PLAN.md): an unattended client retrying
  // on a timeout must not double-book a truck, and a request without a key cannot be
  // made safe to retry. 400, not a silent "create anyway" — the client that omitted it
  // is exactly the one whose retries would duplicate.
  OPERATOR_API_IDEMPOTENCY_KEY_REQUIRED: {
    httpStatus: 400,
    userMessage: 'This request needs an Idempotency-Key header.',
  },
  // The key was already used by this credential for a DIFFERENT request body. Answering
  // with the first request's job would tell the caller its second request succeeded
  // when it was never made; creating a new job would break the key's promise. 422.
  OPERATOR_API_IDEMPOTENCY_KEY_REUSED: {
    httpStatus: 422,
    userMessage: 'This Idempotency-Key was already used for a different request.',
  },
  // Master spec §26's name. The site or well is not a pickup this credential's hauler
  // serves for this operator. ONE code for "exists but is not yours" and "does not
  // exist", deliberately — distinguishing them would let a key enumerate another
  // operator's or another hauler's site ids.
  SITE_NOT_AUTHORIZED: {
    httpStatus: 422,
    userMessage: 'That site is not available to this API key.',
  },

  // --- Phase 21 / Task 21.1 (DEC-327) — platform billing admin API ---
  // The caller holds the permission but its ACTIVE tenant is not the PLATFORM tenant. Platform billing rows are owned by that tenant under RLS, so running under any other tenant would read nothing and write rows it does not own.
  PLATFORM_TENANT_REQUIRED: {
    httpStatus: 403,
    userMessage: 'This action is only available to Paelode Systems staff.',
  },
  // The tenant does not exist, is deleted, or is not a HAULER, OPERATOR or DISPOSAL tenant. One code for all three so the response does not reveal which.
  PLATFORM_BILLING_CUSTOMER_NOT_FOUND: {
    httpStatus: 404,
    userMessage: 'That customer was not found.',
  },
  PLATFORM_RATE_CARD_NOT_FOUND: {
    httpStatus: 404,
    userMessage: 'That rate card was not found.',
  },
  // Names are unique per owner, case-insensitively.
  PLATFORM_RATE_CARD_NAME_TAKEN: {
    httpStatus: 409,
    userMessage: 'A rate card with that name already exists.',
  },
  // No new versions or subscriptions on an archived card.
  PLATFORM_RATE_CARD_ARCHIVED: {
    httpStatus: 409,
    userMessage: 'That rate card is archived. Restore it first.',
  },
  PLATFORM_RATE_CARD_VERSION_NOT_FOUND: {
    httpStatus: 404,
    userMessage: 'That rate card version was not found.',
  },
  // Publishing is one-way; migration 0138 also enforces this with a trigger.
  PLATFORM_RATE_CARD_VERSION_NOT_DRAFT: {
    httpStatus: 409,
    userMessage: 'That version is published and can no longer be changed.',
  },
  PLATFORM_RATE_CARD_VERSION_DATE_TAKEN: {
    httpStatus: 409,
    userMessage: 'Another published version of this rate card already starts on that date.',
  },
  // A published version is frozen and holds its start date for good, so publishing an empty one would burn the date.
  PLATFORM_RATE_CARD_VERSION_EMPTY: {
    httpStatus: 409,
    userMessage: 'Add a line to this version before publishing it.',
  },
  PLATFORM_SUBSCRIPTION_NOT_FOUND: {
    httpStatus: 404,
    userMessage: 'That subscription was not found.',
  },
  PLATFORM_SUBSCRIPTION_OVERLAP: {
    httpStatus: 409,
    userMessage: 'Those dates overlap another subscription for this customer.',
  },
  // Paelode team page — users.email is unique across every kind of account, and one users row
  // has ONE Cognito pool, so an address already in use (a hauler, operator or disposal login)
  // cannot also be a Paelode admin.
  PLATFORM_TEAM_EMAIL_TAKEN: {
    httpStatus: 409,
    userMessage:
      'That email already has a lodeX Pro account. Use a different address, for example name+admin@yourdomain.com.',
  },
  // Paelode team page (review L4) — the address belongs to a removed (soft-deleted) account.
  PLATFORM_TEAM_EMAIL_REMOVED_ACCOUNT: {
    httpStatus: 409,
    userMessage:
      'This address belonged to a removed lodeX Pro account and cannot be reused yet. Use a different address, for example name+admin@yourdomain.com.',
  },
  // Paelode team page — the signed-in admin cannot suspend their own account.
  PLATFORM_TEAM_CANNOT_SUSPEND_SELF: {
    httpStatus: 409,
    userMessage: "You can't suspend yourself. Ask another Paelode admin to do it.",
  },
  // Paelode team page — suspending this admin would leave nobody able to sign in to the console.
  PLATFORM_TEAM_LAST_ADMIN: {
    httpStatus: 409,
    userMessage: 'Paelode must always keep one active admin. Invite another admin first.',
  },
  // Paelode team page — "Resend invite" is for someone who has never set their own password.
  PLATFORM_TEAM_ALREADY_SIGNED_IN: {
    httpStatus: 409,
    userMessage:
      'This admin has already set a password, so no new invite is needed. If they’re locked out, they can use "Forgot password" on the sign-in page.',
  },
  PLATFORM_SUBSCRIPTION_NOT_OPEN: {
    httpStatus: 409,
    userMessage: 'That subscription has already ended.',
  },
  // Undo end: only an ended plan can have its end undone.
  PLATFORM_SUBSCRIPTION_NOT_ENDED: {
    httpStatus: 409,
    userMessage: 'That billing plan has not ended, so there is nothing to undo.',
  },
  // Undo end is for a mis-click: only within minutes of the end, and only while no later plan exists.
  PLATFORM_SUBSCRIPTION_UNDO_EXPIRED: {
    httpStatus: 409,
    userMessage:
      'That end can no longer be undone. Set the billing plan again under Change billing plan.',
  },
  // A hauler rate card cannot be assigned to an operator, and so on; migration 0138 also enforces this with a trigger.
  PLATFORM_SUBSCRIPTION_TENANT_TYPE_MISMATCH: {
    httpStatus: 422,
    userMessage: 'That rate card is for a different kind of customer.',
  },

  // --- DEC-328 (sync nudges) ---
  // The same job was nudged in the last 10 minutes; a second push would only nag.
  SYNC_NUDGE_TOO_SOON: {
    httpStatus: 409,
    userMessage: 'The driver was asked to sync a few minutes ago. Give them a moment.',
  },
  // The job is not in progress on a phone, or has no assigned driver to ask.
  SYNC_NUDGE_NOT_APPLICABLE: {
    httpStatus: 422,
    userMessage: 'Only an in-progress job with an assigned driver can be nudged to sync.',
  },

  // --- DEC-329 (dispatcher-completion approvals) ---
  // The company's policy is NEVER: dispatch may not send a driver's ticket to review.
  DISPATCHER_COMPLETION_DISABLED: {
    httpStatus: 409,
    userMessage: "Your company's settings require the driver to complete this ticket.",
  },
  // A request for this job is already waiting for a manager.
  DISPATCHER_COMPLETION_ALREADY_REQUESTED: {
    httpStatus: 409,
    userMessage: 'This ticket is already waiting for a manager to approve sending it to review.',
  },
  // The request was already approved, declined or cancelled.
  DISPATCHER_COMPLETION_REQUEST_NOT_PENDING: {
    httpStatus: 409,
    userMessage: 'This request has already been decided.',
  },
  // The job left the stuck list (the driver synced, or it lost its load) before approval;
  // the request has been cancelled.
  DISPATCHER_COMPLETION_REQUEST_STALE: {
    httpStatus: 409,
    userMessage: 'This job is no longer stuck, so the request was cancelled. Nothing was changed.',
  },

  // --- Phase 21 / Task 21.4 (DEC-327) — platform invoice generation ---
  PLATFORM_INVOICE_NOT_FOUND: {
    httpStatus: 404,
    userMessage: 'That invoice was not found.',
  },
  // DEC-322 option A: one non-VOID invoice per customer per period. Delete the draft, or void the invoice, to bill the period again.
  PLATFORM_INVOICE_PERIOD_ALREADY_BILLED: {
    httpStatus: 409,
    userMessage: 'This customer already has an invoice for that month.',
  },
  // Only a DRAFT can be regenerated, adjusted, edited or deleted; migration triggers also enforce this.
  PLATFORM_INVOICE_NOT_DRAFT: {
    httpStatus: 409,
    userMessage: 'That invoice has been sent and can no longer be changed.',
  },
  // An adjustment (or regenerating with adjustments kept) would take the total below $0.
  PLATFORM_INVOICE_NEGATIVE_TOTAL: {
    httpStatus: 409,
    userMessage: 'That change would make the invoice total less than zero.',
  },
  // The generator found a skip reason (not active, no subscription, no published version, free, unknown usage, $0). fields[0] carries the reason; the preview shows the detail.
  PLATFORM_INVOICE_NOT_BILLABLE: {
    httpStatus: 422,
    userMessage: 'No invoice can be made for this customer and month. Open the preview to see why.',
  },
  // A TIERED_BAND line has no band covering the meter value, so the price is undefined.
  PLATFORM_INVOICE_NO_MATCHING_BAND: {
    httpStatus: 422,
    userMessage: 'A tiered price has no band for this usage. Fix the rate card, then try again.',
  },
  PLATFORM_INVOICE_ADJUSTMENT_NOT_FOUND: {
    httpStatus: 404,
    userMessage: 'That adjustment was not found on this invoice.',
  },
  // DEC-327 D1: a month is billed once it has ended in the billing timezone. Preview still works.
  PLATFORM_INVOICE_PERIOD_NOT_ENDED: {
    httpStatus: 422,
    userMessage: 'That month has not ended yet. It can be previewed but not billed.',
  },

  // --- Phase 21 / Task 21.5 (DEC-327) — send, PDF, email, void ---
  // DEC-327 D8: the configured sender is outside the allowed sending domains, or SES does not report it (or its domain) verified for sending. Checked BEFORE anything changes; fields name the domain and the reason (NOT_ALLOWED_DOMAIN / NOT_VERIFIED / LOOKUP_FAILED).
  PLATFORM_BILLING_SENDER_NOT_VERIFIED: {
    httpStatus: 409,
    userMessage:
      'The billing sender address cannot send email yet. Check the sender in billing settings.',
  },
  // The shared PDF renderer (task 21.3) has no function name or bucket on this deployment. Nothing was sent or numbered.
  PDF_RENDERER_NOT_CONFIGURED: {
    httpStatus: 503,
    userMessage: 'Invoice PDFs cannot be made on this server right now. Nothing was sent.',
  },
  // A $0 draft (regenerate can leave one) is never sent; delete it instead (DEC-327 D12).
  PLATFORM_INVOICE_ZERO_TOTAL: {
    httpStatus: 422,
    userMessage: 'A $0 invoice cannot be sent. Delete the draft instead.',
  },
  // The billing account has no "to" email address.
  PLATFORM_INVOICE_NO_RECIPIENT: {
    httpStatus: 422,
    userMessage:
      'This customer has no billing email address. Add one to the billing account first.',
  },
  // Resend, reminders, void and the PDF download need a SENT invoice (void: a DRAFT is deleted instead).
  PLATFORM_INVOICE_NOT_SENT: {
    httpStatus: 409,
    userMessage: 'That can only be done to an invoice that has been sent.',
  },
  // v1: a PAID invoice cannot be voided; reverse the payment first (21.6a).
  PLATFORM_INVOICE_PAID_NOT_VOIDABLE: {
    httpStatus: 409,
    userMessage: 'A paid invoice cannot be voided. Reverse the payment first.',
  },
  // The invoice is SENT but its PDF was never stored (the render failed); a resend makes it.
  PLATFORM_INVOICE_PDF_NOT_AVAILABLE: {
    httpStatus: 409,
    userMessage: 'The PDF for this invoice has not been made yet. Resend the invoice to make it.',
  },

  // --- Phase 21 / Task 21.6a (0232) — payments ---
  // A partly paid invoice cannot be voided: its payments are reversed first (Session 6).
  PLATFORM_INVOICE_HAS_PAYMENTS: {
    httpStatus: 409,
    userMessage: 'This invoice has payments recorded. Reverse them before voiding it.',
  },
  // A payment goes against a SENT invoice that still has a balance (not a draft, void or paid one).
  PLATFORM_INVOICE_NOT_PAYABLE: {
    httpStatus: 409,
    userMessage: 'A payment can only be recorded against a sent invoice that still has a balance.',
  },
  // v1 refuses an overpayment rather than holding a credit.
  PLATFORM_PAYMENT_EXCEEDS_BALANCE: {
    httpStatus: 409,
    userMessage: 'That is more than the invoice still owes. Overpayments are not recorded.',
  },
  PLATFORM_PAYMENT_NOT_FOUND: {
    httpStatus: 404,
    userMessage: 'That payment was not found.',
  },
  // Only a PAYMENT row can be reversed, and only once.
  PLATFORM_PAYMENT_NOT_REVERSIBLE: {
    httpStatus: 409,
    userMessage: 'This payment has already been reversed, or is itself a reversal.',
  },

  // --- OPEN-212 PR B (0272, DEC-387) — paying an invoice by ACH through Stripe ---
  // A debit the customer authorised is settling (days for ACH): recording money by hand, voiding
  // or starting another payment now could count the same money twice.
  PLATFORM_INVOICE_PAYMENT_IN_PROGRESS: {
    httpStatus: 409,
    userMessage:
      'A bank payment for this invoice is being processed. Wait for it to settle or fail first.',
  },
  // The environment has no Stripe keys yet (or only a live key without STRIPE_LIVE=1).
  STRIPE_NOT_CONFIGURED: {
    httpStatus: 503,
    userMessage: 'Online payment is not set up yet.',
  },
  STRIPE_REQUEST_FAILED: {
    httpStatus: 502,
    userMessage: 'Stripe did not answer. Nothing was charged; try again in a minute.',
  },
  PLATFORM_PAYMENT_ATTEMPT_NOT_FOUND: {
    httpStatus: 404,
    userMessage: 'That payment link was not found.',
  },
  // Only a link nobody has used yet can be cancelled; a submitted debit settles or fails.
  PLATFORM_PAYMENT_ATTEMPT_NOT_CANCELABLE: {
    httpStatus: 409,
    userMessage: 'That payment link has already been used or closed.',
  },

  // --- #56 PR 2a (DEC-363) — ops alert recipients and incidents ---
  // D-5: a recipient is an ACTIVE PLATFORM_ADMIN of the PLATFORM tenant (a trigger enforces it too).
  OPS_ALERT_RECIPIENT_NOT_PLATFORM_ADMIN: {
    httpStatus: 422,
    userMessage: 'Only an active Paelode platform admin can receive ops alerts.',
  },
  OPS_ALERT_RECIPIENT_EXISTS: {
    httpStatus: 409,
    userMessage: 'That person already receives ops alerts.',
  },
  // Ack needs an OPEN incident; resolve needs an OPEN or ACKED one.
  OPS_ALERT_INCIDENT_NOT_LIVE: {
    httpStatus: 409,
    userMessage: 'That incident has already been handled. Refresh to see its current state.',
  },

  // --- #60 / DEC-365 — changing a job's pickup site ---
  // Approved for billing (or cancelled, rejected, voided): the site is locked. To correct an
  // approved ticket, send it back to review first.
  SITE_CHANGE_LOCKED: {
    httpStatus: 409,
    userMessage: "This job's site can't be changed any more. Send the ticket back to review first.",
  },
  // D2 — only a site of the job's own customer; anything else is cancel/void and recreate.
  SITE_CUSTOMER_MISMATCH: {
    httpStatus: 400,
    userMessage: "Pick a site that belongs to this job's customer.",
  },
  // The site changed since the caller loaded the job (another dispatcher, or the driver).
  SITE_CHANGED_ELSEWHERE: {
    httpStatus: 409,
    userMessage: "This job's site was just changed by someone else. Reload the job and try again.",
  },

  // #56 PR 3b — the raw log lookup cannot run here: no log group configured (off AWS),
  // CloudWatch Logs refused or failed, or Redis (which binds a query to its admin) is down.
  OPS_LOG_LOOKUP_UNAVAILABLE: {
    httpStatus: 503,
    userMessage: 'The raw log lookup is not available right now. The Errors list still works.',
  },

  // --- Operator portal rework PR 5 — "view as operator" (services/view-as.ts) ---
  // A view-as session is read-only: anything but an operator portal read is refused on the server.
  VIEW_AS_READ_ONLY: {
    httpStatus: 403,
    userMessage: 'You are viewing this portal as someone else. Changes are turned off.',
  },
  // The view-as code or token is unknown, already used, ended or expired.
  VIEW_AS_SESSION_INVALID: {
    httpStatus: 401,
    userMessage: 'This view-as session has ended. Start a new one from the admin console.',
  },
  // The user to view as is not an active member of that operator company (or it has none).
  VIEW_AS_TARGET_INVALID: {
    httpStatus: 409,
    userMessage: 'There is no active user in that company to view the portal as.',
  },

  // --- Operator portal rework PR 12a (0222) — a pumper turns a load in ---
  // The chosen hauler cannot take this request: an EXPRESS hauler works for one other customer.
  HAUL_REQUEST_HAULER_UNAVAILABLE: {
    httpStatus: 409,
    userMessage:
      "That hauler isn't taking requests for your company right now. Choose another hauler, or ask your admin.",
  },

  // gap 1 PR 2 / OPEN-213: stale bulk price preview (one status, agreed with S4). The preview token no
  // longer matches (prices changed, another user's token, a modified request), the preview is over 30
  // minutes old, or a concurrent write took the same day key (23505).
  PRICE_PREVIEW_STALE: {
    httpStatus: 409,
    userMessage: 'Prices changed since the preview. Preview again.',
  },
} as const satisfies Record<string, { httpStatus: number; userMessage: string }>;

/**
 * Union type of all valid API error codes. Clients import this type so they
 * can safely branch on `response.error.code` without stringly-typed comparisons.
 */
export type ErrorCode = keyof typeof ERROR_CATALOG;

/**
 * Per-field validation failure entry — D-16's Zod translation produces these.
 *
 * `path` mirrors ZodError.issues[i].path joined by '.', e.g. 'body.volumeBbl'.
 * `reason` mirrors ZodError.issues[i].code, e.g. 'too_small'.
 */
export interface ErrorField {
  path: string;
  reason: string;
}

/**
 * D-13 — every API error response has this shape, regardless of HTTP status.
 *
 * Plan 07's global setErrorHandler emits this shape. Mobile/web parse it.
 * The HTTP status code comes from ERROR_CATALOG[code].httpStatus; the
 * user-safe message comes from ERROR_CATALOG[code].userMessage.
 *
 * `refCode` — 'ERR-XXXX' — 4-char base32 slice of the internal errorId ULID
 * (D-15). Drivers see this in the UI; support searches CloudWatch for it.
 *
 * `requestId` — 'req_<ULID>' — set by the requestId Fastify plugin in
 * onRequest (D-17). Present in every Pino log line and every error response.
 *
 * `fields` — populated only when code === 'VALIDATION_FAILED' (D-16 Zod
 * translation). Absent (not null, not []) for all other error codes.
 */
export interface ErrorResponse {
  error: {
    code: ErrorCode;
    message: string;
    refCode: string;
    fields?: ErrorField[];
  };
  requestId: string;
}
